import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { register, validateEnterpriseCode } from '../api/auth';
import { CenteredLayout } from '../components/PageLayout';
import Card from '../components/Card';
import Input from '../components/Input';
import Button from '../components/Button';
import Alert from '../components/Alert';
import { parseApiError } from '../utils/parseApiError';

const REGISTER_FIELD_LABELS = {
  email: '邮箱', password: '密码', password_confirm: '确认密码',
  display_name: '姓名', enterprise_code: '企业码',
};

export default function RegisterPage() {
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();
  // Phase G (2026-04-24)：注册不再要工作方向，登录后问卷自动派生
  const [form, setForm] = useState({ email:'', display_name:'', password:'', password_confirm:'', enterprise_code:'' });
  const [enterpriseInfo, setEnterpriseInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeChecking, setCodeChecking] = useState(false);

  const set = (k) => (e) => setForm({...form, [k]: e.target.value});
  const setCode = (e) => setForm({...form, enterprise_code: e.target.value.toUpperCase()});

  const handleCodeBlur = async () => {
    const code = form.enterprise_code.trim();
    if (!code) { setEnterpriseInfo(null); return; }
    setCodeChecking(true);
    try {
      const { data } = await validateEnterpriseCode(code);
      setEnterpriseInfo(data);  // 含 name / classes_enabled / class_name? / join_code?
    } catch (err) {
      const errorCode = err?.response?.data?.error_code;
      const messages = {
        class_required: '该企业已启用班级，请使用含班级编号的完整码（如 ACME-00）',
        class_disabled: '该班级已停用，请联系企业管理员获取新的注册码',
        invalid_code: '企业码无效，请检查后重试',
      };
      setEnterpriseInfo({ error: messages[errorCode] || '企业码无效，请检查后重试' });
    } finally { setCodeChecking(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.password_confirm) { setError('两次密码不一致'); return; }
    setError(''); setLoading(true);
    try {
      const { data } = await register(form);
      authLogin(data.user, data.access, data.refresh);
      // 2026-05-22：注册后落首页,由 HomePage 上的 ProfileNudgeModal 引导填画像
      // (旧行为是 navigate('/profile/setup') 全屏跳转,新用户看不到 modal 体验)
      navigate('/');
    } catch (err) {
      // QA 2026-04-29 批次一 F1：错误处理统一走 parseApiError，避免每页一份重写。
      // 关键修复：err.response 为空（ERR_CONNECTION_CLOSED）时不再显示"检查网络连接"，
      // 改成"服务器连接异常"以反映 P0 生产侧抖动的真实原因。
      setError(parseApiError(err, {
        fieldLabels: REGISTER_FIELD_LABELS,
        fallback: '注册失败，请检查填写内容',
      }));
    } finally { setLoading(false); }
  };

  return (
    <CenteredLayout>
      <div style={{textAlign:'center',marginBottom:32}}>
        <Link to="/" style={{
          display:'inline-flex',alignItems:'center',gap:9,
          textDecoration:'none',
          fontWeight:800,fontSize:20,color:'#0e1730',letterSpacing:'0.01em',
        }}>
          <span style={{
            width:32,height:32,
            background:'linear-gradient(135deg, #1f53e6, #0d2b8a)',color:'#fff',
            borderRadius:9,
            display:'inline-flex',alignItems:'center',justifyContent:'center',
            fontFamily:'Consolas, "SF Mono", monospace',fontSize:14,fontWeight:800,
          }}>Q</span>
          前哨科技
          <span style={{color:'#6a7596',fontFamily:'Consolas, "SF Mono", monospace',fontWeight:500,letterSpacing:'0.04em',fontSize:12,marginLeft:2}}>QianShao.AI</span>
        </Link>
        <h1 style={{fontSize:24,fontWeight:800,marginTop:18,marginBottom:6,color:'#0e1730'}}>创建账号</h1>
        <p style={{color:'#6a7596',fontSize:13.5,margin:0}}>开始你的 AI 领导力测评之旅</p>
      </div>
      <Card>
        {error && <Alert type="error">{error}</Alert>}
        <form onSubmit={handleSubmit}>
          <Input label="邮箱" type="email" value={form.email} required onChange={set('email')} placeholder="your@email.com"/>
          <Input label={enterpriseInfo && !enterpriseInfo.error ? "真实姓名（企业模式必填）" : "姓名 / 昵称"} value={form.display_name} required onChange={set('display_name')} placeholder="你的名字"/>
          <Input label="密码" type="password" value={form.password} required onChange={set('password')} placeholder="至少8位"/>
          <Input label="确认密码" type="password" value={form.password_confirm} required onChange={set('password_confirm')} placeholder="再次输入密码"/>
          {/* Phase G (2026-04-24)：注册不再要求工作方向；登录后通过工作特征问卷自动判定 */}
          <div style={{marginBottom:18}}>
            <Input label="企业码（可选）" value={form.enterprise_code} onChange={setCode}
              onBlur={handleCodeBlur} placeholder="4~8位字母数字，如 ACME2024" style={{marginBottom:0}}/>
            <p style={{fontSize:11,color:'var(--color-text-muted)',marginTop:4}}>4~8位字母或数字，字母自动转大写</p>
            {codeChecking && <p style={{fontSize:12,color:'var(--color-text-muted)',marginTop:4}}>验证中...</p>}
            {enterpriseInfo && !enterpriseInfo.error && (
              <Alert type="success">
                已识别企业：{enterpriseInfo.name}
                {enterpriseInfo.classes_enabled
                  ? `，将加入班级 ${enterpriseInfo.class_name}`
                  : '，将以全员模式参与测评（不分班）'}
              </Alert>
            )}
            {enterpriseInfo?.error && <Alert type="error">{enterpriseInfo.error}</Alert>}
          </div>
          <Button type="submit" fullWidth loading={loading}>注册</Button>
        </form>
      </Card>
      <p style={{textAlign:'center',marginTop:20,fontSize:13.5,color:'#6a7596'}}>
        已有账号？<Link to="/login" style={{color:'#4f46e5',fontWeight:600,textDecoration:'none'}}>立即登录</Link>
      </p>
    </CenteredLayout>
  );
}
